<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import PortalStepsEditor from "./_modules/portal-steps-editor"
  import SignatureDraftSection from "./_modules/signature-draft-section"
  import type { LetterDraft } from "../../_shared/_logic/letter-types"
  import type { KehrblattBusinessProfile } from "../../../../../../../../../../api/kehrblatt/business-profile"

  let {
    organizationId,
    draft = $bindable(),
    profile,
    disabled,
  }: {
    organizationId: string
    draft: LetterDraft
    profile: KehrblattBusinessProfile
    disabled: boolean
  } = $props()
  const senderFields = [
    ["name", "Name"],
    ["street", "Straße"],
    ["house_number", "Hausnummer"],
    ["address_extra", "Adresszusatz"],
    ["zip", "Postleitzahl"],
    ["city", "Ort"],
    ["country", "Land"],
  ] as const
  const windowFields = [
    ["sender_left_mm", "Absender links (mm)"],
    ["sender_top_mm", "Absender oben (mm)"],
    ["sender_width_mm", "Absender Breite (mm)"],
    ["sender_height_mm", "Absender Höhe (mm)"],
    ["recipient_left_mm", "Empfänger links (mm)"],
    ["recipient_top_mm", "Empfänger oben (mm)"],
    ["recipient_width_mm", "Empfänger Breite (mm)"],
    ["recipient_height_mm", "Empfänger Höhe (mm)"],
  ] as const
</script>

<div class="form common-settings">
  <section aria-label="Absenderadresse">
    <SectionTitle title="Absenderadresse" brightBackground={true} small={true} />
    <Input
      type="select"
      placeholder="Adresse"
      required={true}
      bind:value={draft.personal_letter_sender.source}
      {disabled}
      brightBackground={true}
      options={[
        { value: "PUBLIC_INFORMATION", label: "Aus Betriebsangaben übernehmen" },
        { value: "CUSTOM", label: "Eigene Absenderadresse angeben" },
      ]}
    />
    {#if draft.personal_letter_sender.source === "CUSTOM"}
      <div class="field-grid">
        {#each senderFields as [key, label] (key)}
          <Input
            type="text"
            placeholder={label}
            bind:value={draft.personal_letter_sender.custom_address[key]}
            brightBackground={true}
            {disabled}
          />
        {/each}
      </div>
    {:else}
      <p
        >{profile.organization_name}<br />{profile.address?.street}
        {profile.address?.house_number}<br />
        {profile.address?.zip}
        {profile.address?.city}</p
      >
    {/if}
    <p>Die Absenderadresse gilt gemeinsam für Ankündigungs- und Verschiebungsbriefe.</p>
  </section>
  <SignatureDraftSection {organizationId} bind:signature={draft.letter_signature} {disabled} />
  <section aria-label="Fensterbriefkonfiguration">
    <SectionTitle
      title="Fensterbriefkonfiguration"
      brightBackground={true}
      small={true}
      helpModalTextId="planningLetterWindow"
    />
    <p>Positionen in Millimetern relativ zur A4-Seite für Absender- und Empfängerfeld.</p>
    <div class="field-grid">
      {#each windowFields as [key, label] (key)}
        <Input
          type="number"
          placeholder={label}
          bind:value={draft.letter_address_window[key]}
          brightBackground={true}
          {disabled}
          min={1}
        />
      {/each}
    </div>
  </section>
  <section aria-label="Portalschritte">
    <SectionTitle title="Portalschritte" brightBackground={true} small={true} />
    <PortalStepsEditor bind:copy={draft.portal} {disabled} />
  </section>
  <section aria-label="Rückmeldehinweis">
    <SectionTitle title="Rückmeldehinweis" brightBackground={true} small={true} />
    <Input
      type="textarea"
      placeholder="Hinweis zur Rückmeldung"
      bind:value={draft.portal.response_hint}
      brightBackground={true}
      {disabled}
    />
  </section>
  <section aria-label="Kontaktbox">
    <SectionTitle title="Kontaktbox" brightBackground={true} small={true} />
    <Input
      type="text"
      placeholder="Überschrift der Kontaktbox"
      bind:value={draft.portal.support_heading}
      brightBackground={true}
      {disabled}
    />
    <Input
      type="tagbased_input"
      placeholder="Text der Kontaktbox"
      bind:value={draft.portal.support_body}
      tokenSyntax="single"
      options={[{ label: "Betriebskontakt", value: "contact_details" }]}
      brightBackground={true}
      {disabled}
    />
  </section>
</div>

<style>
  .common-settings,
  section {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    min-width: 0;
    height: auto;
    color: var(--text-invers-100);
  }
  .common-settings {
    gap: 2rem;
  }
  .common-settings > :global(*),
  section > :global(*) {
    min-width: 0;
    max-width: 100%;
  }
  .field-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
    gap: 1rem;
  }
  p {
    margin: 0;
    line-height: 1.5;
    font-size: 0.85rem;
    color: var(--text-invers-100);
  }
</style>
